/**
 * Location Modal (ENH-004)
 * ZIP code entry modal for delivery validation
 */
import { useState, Fragment, useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { MapPinIcon, XMarkIcon, TruckIcon, ClockIcon } from '@heroicons/react/24/outline';
import { CheckCircleIcon, ExclamationTriangleIcon } from '@heroicons/react/24/solid';
import { useLocation } from '../../contexts/LocationContext';

interface LocationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export default function LocationModal({ isOpen, onClose, onSuccess }: LocationModalProps) {
  const { setZipCode, deliveryInfo, isLoading, zipCode: savedZipCode } = useLocation();
  const [inputZip, setInputZip] = useState('');
  const [showResult, setShowResult] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const [notifyEmail, setNotifyEmail] = useState('');
  const [emailSubmitted, setEmailSubmitted] = useState(false);
  const [geoLoading, setGeoLoading] = useState(false);
  const [coordinates, setCoordinates] = useState<{ lat: number; lng: number } | null>(null);

  // Initialize with saved ZIP and show result if we have delivery info
  useEffect(() => {
    if (isOpen) {
      // Preserve the saved ZIP code
      if (savedZipCode) {
        setInputZip(savedZipCode);
        // Show result immediately if we have delivery info
        if (deliveryInfo) {
          setShowResult(true);
        }
      } else {
        setInputZip('');
        setShowResult(false);
      }
      setLocalError(null);
      setNotifyEmail('');
      setEmailSubmitted(false);
    }
  }, [isOpen, savedZipCode, deliveryInfo]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (inputZip.length >= 5) {
      try {
        setLocalError(null);
        await setZipCode(inputZip);
        setShowResult(true);
      } catch (err: any) {
        setLocalError(err.message || 'Failed to check delivery availability');
      }
    }
  };

  const handleContinue = () => {
    if (deliveryInfo?.is_eligible) {
      onSuccess?.();
      onClose();
    }
  };

  const handleNotifySubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // TODO: Submit to waitlist API
    setEmailSubmitted(true);
  };

  // Request browser geolocation
  const handleUseMyLocation = async () => {
    if (!navigator.geolocation) {
      setLocalError('Geolocation is not supported by your browser');
      return;
    }

    setGeoLoading(true);
    setLocalError(null);

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        setCoordinates({ lat: latitude, lng: longitude });

        // Try to reverse geocode to get ZIP (using free API)
        try {
          const response = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&addressdetails=1`
          );
          const data = await response.json();
          const postcode = data?.address?.postcode;

          if (postcode && postcode.length >= 5) {
            setInputZip(postcode.slice(0, 5));
          }
        } catch (geoError) {
          console.error('Reverse geocoding failed:', geoError);
          // Still have coordinates for accurate ETA, just no ZIP auto-fill
        }

        setGeoLoading(false);
      },
      (geoError) => {
        setGeoLoading(false);
        switch (geoError.code) {
          case geoError.PERMISSION_DENIED:
            setLocalError('Location access denied. Please enter your ZIP code manually.');
            break;
          case geoError.POSITION_UNAVAILABLE:
            setLocalError('Location unavailable. Please enter your ZIP code manually.');
            break;
          case geoError.TIMEOUT:
            setLocalError('Location request timed out. Please try again or enter ZIP manually.');
            break;
          default:
            setLocalError('Could not get location. Please enter your ZIP code manually.');
        }
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
    );
  };

  const resetForm = () => {
    setInputZip('');
    setShowResult(false);
    setLocalError(null);
    setNotifyEmail('');
    setEmailSubmitted(false);
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/60" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-[calc(100%-32px)] sm:w-full max-w-md transform overflow-hidden rounded-2xl bg-white shadow-xl transition-all">
                {/* Header */}
                <div className="relative bg-black px-6 py-8 text-center">
                  <button
                    onClick={onClose}
                    className="absolute right-4 top-4 text-white/70 hover:text-white transition-colors"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                  <div className="mx-auto w-16 h-16 bg-white/10 rounded-full flex items-center justify-center mb-4">
                    <MapPinIcon className="h-8 w-8 text-white" />
                  </div>
                  <Dialog.Title className="text-2xl font-heading text-white tracking-wide">
                    WHERE ARE WE DELIVERING?
                  </Dialog.Title>
                  <p className="mt-2 text-sm text-white/70">
                    Enter your ZIP code to see delivery options
                  </p>
                </div>

                {/* Content */}
                <div className="px-6 py-6">
                  {!showResult ? (
                    // ZIP Input Form
                    <div className="space-y-4">
                      {/* Use My Location Button */}
                      <button
                        type="button"
                        onClick={handleUseMyLocation}
                        disabled={geoLoading}
                        className="w-full flex items-center justify-center gap-3 py-4 border-2 border-gray-200 rounded-xl hover:border-black hover:bg-gray-50 transition-all group"
                      >
                        {geoLoading ? (
                          <>
                            <div className="animate-spin rounded-full h-5 w-5 border-2 border-black border-t-transparent" />
                            <span className="font-medium text-gray-700">Locating...</span>
                          </>
                        ) : (
                          <>
                            <div className="w-10 h-10 bg-black rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
                              <MapPinIcon className="h-5 w-5 text-white" />
                            </div>
                            <span className="font-medium text-gray-900">Use My Location</span>
                          </>
                        )}
                      </button>

                      <div className="flex items-center gap-3">
                        <div className="flex-1 h-px bg-gray-200" />
                        <span className="text-xs text-gray-400 uppercase tracking-wide">or enter ZIP</span>
                        <div className="flex-1 h-px bg-gray-200" />
                      </div>

                      <form onSubmit={handleSubmit}>
                        <div className="relative">
                          <input
                            type="text"
                            value={inputZip}
                            onChange={(e) => setInputZip(e.target.value.replace(/\D/g, '').slice(0, 5))}
                            placeholder="Enter ZIP code"
                            className="w-full px-4 py-4 text-lg text-center border-2 border-gray-200 rounded-xl focus:border-black focus:ring-0 font-mono tracking-widest transition-colors"
                            maxLength={5}
                          />
                        </div>

                        {localError && (
                          <p className="mt-2 text-sm text-red-600 text-center">{localError}</p>
                        )}

                        {coordinates && (
                          <p className="mt-2 text-xs text-green-600 text-center">
                            ✓ Location detected for accurate delivery estimate
                          </p>
                        )}

                        <button
                          type="submit"
                          disabled={inputZip.length < 5 || isLoading}
                          className="w-full mt-4 py-4 bg-black text-white font-medium rounded-xl hover:bg-gray-800 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                        >
                          {isLoading ? (
                            <span className="flex items-center justify-center gap-2">
                              <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
                              Checking...
                            </span>
                          ) : (
                            'Check Delivery'
                          )}
                        </button>
                      </form>
                    </div>
                  ) : deliveryInfo?.is_eligible ? (
                    // Success - Delivery Available
                    <div className="text-center">
                      <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                        <CheckCircleIcon className="h-10 w-10 text-green-600" />
                      </div>
                      <h3 className="text-xl font-semibold text-black">
                        Great news! We deliver to {deliveryInfo.zip_code}
                      </h3>

                      <div className="mt-6 grid grid-cols-2 gap-4">
                        <div className="bg-gray-50 rounded-xl p-4">
                          <TruckIcon className="h-6 w-6 text-black mx-auto mb-2" />
                          <p className="text-sm text-gray-500">Delivery</p>
                          <p className="font-semibold text-black text-sm">
                            {deliveryInfo.delivery_window?.display_text || 'Available'}
                          </p>
                        </div>
                        <div className="bg-gray-50 rounded-xl p-4">
                          <ClockIcon className="h-6 w-6 text-black mx-auto mb-2" />
                          <p className="text-sm text-gray-500">Est. Time</p>
                          <p className="font-semibold text-black text-sm">
                            {deliveryInfo.eta_display || '30-60 min'}
                          </p>
                        </div>
                      </div>

                      {deliveryInfo.delivery_window?.is_same_day && (
                        <p className="mt-4 text-sm text-green-600 font-medium">
                          Order now for same-day delivery!
                        </p>
                      )}

                      <div className="mt-6 flex gap-3">
                        <button
                          onClick={resetForm}
                          className="flex-1 py-3 border-2 border-gray-200 text-gray-700 font-medium rounded-xl hover:bg-gray-50 transition-colors"
                        >
                          Change
                        </button>
                        <button
                          onClick={handleContinue}
                          className="flex-1 py-3 bg-black text-white font-medium rounded-xl hover:bg-gray-800 transition-colors"
                        >
                          Continue
                        </button>
                      </div>
                    </div>
                  ) : (
                    // Not Available
                    <div className="text-center">
                      <div className="mx-auto w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mb-4">
                        <ExclamationTriangleIcon className="h-10 w-10 text-amber-600" />
                      </div>
                      <h3 className="text-xl font-semibold text-black">
                        We're not in {inputZip} yet
                      </h3>
                      <p className="mt-2 text-gray-600">
                        We're expanding soon! Leave your email and we'll notify you when we arrive.
                      </p>

                      {!emailSubmitted ? (
                        <form className="mt-4" onSubmit={handleNotifySubmit}>
                          <input
                            type="email"
                            value={notifyEmail}
                            onChange={(e) => setNotifyEmail(e.target.value)}
                            placeholder="your@email.com"
                            className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-black focus:ring-0 transition-colors"
                            required
                          />
                          <button
                            type="submit"
                            className="w-full mt-3 py-3 bg-black text-white font-medium rounded-xl hover:bg-gray-800 transition-colors"
                          >
                            Notify Me
                          </button>
                        </form>
                      ) : (
                        <div className="mt-4 p-4 bg-green-50 rounded-xl">
                          <CheckCircleIcon className="h-8 w-8 text-green-600 mx-auto mb-2" />
                          <p className="text-green-800 font-medium">
                            We'll let you know when we're in your area!
                          </p>
                        </div>
                      )}

                      <button
                        onClick={resetForm}
                        className="mt-4 text-sm text-gray-500 hover:text-black transition-colors"
                      >
                        Try a different ZIP code
                      </button>
                    </div>
                  )}
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
